import React, { Suspense, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { InputGroup, Panel } from 'react-bootstrap';
import { navigate } from 'raviger';
import _ from 'lodash';

import withAuthentication from '../../hoc/withAuthentication';
import withLayout from '../../hoc/withLayout';

import { useAlertStore } from '../../store/providers/Alert';
import { useFranchiseStore } from '../../store/providers/Franchise';
import Permission from '../../components/Permission/Permission';

import { breadcrumbAddRoute, breadcrumbRemoveRoute } from '../../store/actions/breadcrumb';
import { getListState, saveListState } from '../../lib/ListStateHelper';

import { useTransactionDocumentLookupsStore } from '../../store/providers/TransactionDocumentLookups';

import Spinner from '../../components/Spinner/Spinner';

import * as constants from '../../constants';
import { columns, sorting } from './FranchiseListSchema';
import FormInput from '../../components/FormInput/FormInput';
import OptionsHelper from '../../lib/OptionsHelper';
import DocumentSetupList from '../../components/Lists/DocumentSetupList/DocumentSetupList';
const SideMenu = React.lazy(() => import('../../components/SideMenu/SideMenu'));
const PageHeader = React.lazy(() => import('../../components/PageHeader/PageHeader'));

function Franchises() {
    const dispatch = useDispatch();

    const listKey = '/instances';
    const savedListState = getListState(listKey) || {};

    const { addAlert } = useAlertStore();
    const { listRecords, deleteRecord, data, clearData } = useFranchiseStore();
    const { setStatusKey: setSelectedStatus, setDocumentCategoryKey: setSelectedDocumentCategory,
        setProspectsStatusKey: setSelectedProspectsStatus, setFranchiseTabKey: setSelectedFranchiseTab } = useTransactionDocumentLookupsStore();

    const [isMounted, setIsMounted] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [listData, setListData] = useState([]);
    const [selectedType, setSelectedType] = useState(savedListState.typeId || '');
    const [instanceTypeList] = useState(['Corporate', 'Non Corporate', 'BrokerOps']);
    const [instanceTypeOptions, setInstanceTypeOptions] = useState();

    const pagePermission = constants.PERMISSION_FRANCHISES;
    const [hasPermission, setHasPermission] = useState(false);
    const [canEdit, setCanEdit] = useState(false);

    const selectedHandler = (franchise) => {
        resetDefaultFranchiseTab();
        navigate(`/instances/edit/${franchise.id}`);
    };

    const addedHandler = () => {
        resetDefaultFranchiseTab();
        navigate(`/instances/new`);
    };

    const changedHandler = (id, value) => {
        const typeId = value ? value : '';

        if (typeId === 'Corporate' || typeId === 'Non Corporate' || typeId === 'BrokerOps') {
            setSelectedType(typeId);
        } else {
            setSelectedType('');
        }

        saveListState(listKey, { typeId });
        listRecords();
    }

    const moreFilters = (instanceTypeOptions) => {
        return (
            <>
                <div style={{ paddingLeft: '1%' }}>
                    <InputGroup style={{ width: '270px' }}>
                        <InputGroup.Addon>Instance Type:</InputGroup.Addon>
                        <FormInput 
                            id="typeId"
                            type="select"
                            required={false}
                            labelText=''
                            errorMessage={''}
                            changed={(id, value) => changedHandler(id, value)}
                            value={selectedType || ''}
                        >
                            {instanceTypeOptions}
                        </FormInput>
                    </InputGroup>
                </div>
            </>
        )
    }

    const resetDefaultFranchiseTab = () => {
        setSelectedStatus(constants.DEFAULT_STATUS_KEY);
        setSelectedProspectsStatus(constants.DEFAULT_STATUS_KEY);
        setSelectedFranchiseTab(constants.DEFAULT_FRANCHISE_TAB);
        setSelectedDocumentCategory(constants.DOCUMENT_CATEGORY.Transaction);
    };

    const deletedHandler = (franchise) => {
        deleteRecord({ franchiseId: franchise.id });
    };

    const refreshedHandler = () => {
        listRecords();
    };

    useEffect(() => {
        if (!_.isEmpty(instanceTypeList)) {
            let listInstanceTypeOptions = [];
            instanceTypeList.forEach((a) => {
                listInstanceTypeOptions.push({id: a, name: a});             
            });
           setInstanceTypeOptions(OptionsHelper({ list: listInstanceTypeOptions, includeBlank: true, includeBlankName: 'All' }));
        }
    }, [instanceTypeList]);

    useEffect(() => {
        if (!_.isEmpty(data)) {
            if (data.status === 'fail' || data.status === 'error') {
                addAlert({ key: constants.ALERT_SERVER_ERROR, text: <strong>{data.body}</strong>, type: constants.ALERT_TYPE_DANGER });
                clearData();
            }

            if (data.scope === 'delete' && data.status === 'success') {
                listRecords();
            }

            if (data.status === 'success' && data.scope === 'list') {

                var listInfo = data.body;
                if(selectedType){
                    if(selectedType == 'Corporate') {
                        listInfo = data.body.filter((e) => e.corporateOwned == true && (e.brokerOps == false || e.brokerOps == null));
                    } else if(selectedType == 'Non Corporate') {
                        listInfo = data.body.filter((e) => e.corporateOwned == false && (e.brokerOps == false || e.brokerOps == null));
                    } else if(selectedType == 'BrokerOps') {
                        listInfo = data.body.filter((e) => e.brokerOps == true );
                    }
                }
                setListData(listInfo);

            }

            setIsBusy(data.status === 'processing' && (data.scope === 'delete' || data.scope === 'list'));
        }
    }, [data, addAlert, clearData, listRecords, selectedType]);

    useEffect(() => {
        if (!hasPermission) { return; }
        if (isMounted) { return; }

        dispatch(breadcrumbAddRoute({ key: 'instances', text: 'Instances', path: window.location.pathname }));
        listRecords();

        setIsMounted(true);
    }, [isMounted, hasPermission, listRecords, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(breadcrumbRemoveRoute());
        };
    }, [dispatch]);

    return (
        <>
            <SideMenu activeKey="franchises" />
            <div className="content-body">
                <Permission showDenied={true} pagePermission={pagePermission} hasPermission={hasPermission} setHasPermission={setHasPermission} setCanEdit={setCanEdit} />
                {hasPermission && isMounted &&
                    <Suspense fallback={<Spinner text="Loading..." />}>
                        <div style={{ paddingRight: '15px' }}>
                            <PageHeader title="Instances" />
                            <Panel>
                                <Panel.Body>
                                    <DocumentSetupList
                                        compType="Instance"
                                        listData={listData}
                                        isBusy={isBusy}
                                        sorting={sorting}
                                        columns={columns}
                                        hideCopy={true}
                                        hidePrint={true}
                                        permissionEdit={canEdit}
                                        activeValue="Active"
                                        activeField="statusName"
                                        added={() => addedHandler()}
                                        moreFilters={moreFilters(instanceTypeOptions)}
                                        selected={(franchise) => selectedHandler(franchise)}
                                        deleted={(franchise) => deletedHandler(franchise)}
                                        refreshed={() => refreshedHandler()} />
                                </Panel.Body>
                            </Panel>
                        </div>
                    </Suspense>
                }
            </div>
        </>
    );
}

export default withAuthentication(withLayout(Franchises));
